import { z } from "zod";

// ---------------------------------------------------------------------------
// Web Push payload contract
//
// The shape the backend's push sender serializes into each encrypted push
// message and the service worker parses on receipt. Distinct from the SignalR
// realtime wire (notifications.ts): a push must be self-contained because the
// service worker cannot make an authenticated API call to enrich it. Title and
// body are computed server-side (single place for presentation rules — e.g.
// "outstanding question" phrasing for messages); the structured nav fields let
// the worker build the in-app deep link without a fetch.
//
// Parsed at the boundary (service worker) with safeParse — a malformed or
// drifted payload is dropped rather than rendered as a broken toast.
// ---------------------------------------------------------------------------

export const PushNotificationPayloadSchema = z.object({
    title: z.string(),
    body: z.string(),
    workflowId: z.string(),
    workItemId: z.string(),
    entityType: z.string(),
    entityId: z.string(),
    // Notification grouping/coalescing key. Defaults to the work-item id on
    // the worker side when absent so repeated pushes for one item collapse.
    tag: z.string().optional(),
    // The recipient's outstanding work-item count *after* this item landed,
    // computed per-recipient server-side. Lets the worker repaint the OS app
    // badge from the push (the one Windows-shell repaint path that works while
    // the running app's own setAppBadge calls are ignored). Optional: omitted
    // when the server-side count could not be computed, in which case the
    // worker leaves the existing badge untouched rather than guessing.
    outstandingCount: z.number().int().nonnegative().optional(),
    // Server time the count was read at (ISO 8601). Travels with the count so
    // the worker can drop a late-delivered push that is older than a badge
    // write it has already applied. Omitted together with outstandingCount.
    outstandingAsOf: z.string().optional(),
});

export type PushNotificationPayload = z.infer<typeof PushNotificationPayloadSchema>;
